import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { getCurrentSchool, ASSETS } from '../config/schools';

/**
 * BreathingBreak - Composant de pause "respiration" après 10 minutes d'activité
 * Affiche l'animation du logo avec les super-héros puis retourne exactement au même endroit
 */
const BreathingBreak = ({ 
  children, 
  breakInterval = 10 * 60 * 1000, // 10 minutes par défaut
  enabled = true,
  onBreakStart,
  onBreakEnd
}) => {
  const [showBreak, setShowBreak] = useState(false);
  const [stage, setStage] = useState(0);
  const [flash, setFlash] = useState(false);
  const [shake, setShake] = useState(false);
  const [activityTime, setActivityTime] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  
  const school = getCurrentSchool();

  // Timer d'activité
  useEffect(() => {
    if (!enabled || showBreak || isPaused) return;

    const interval = setInterval(() => {
      setActivityTime(prev => {
        const newTime = prev + 1000;
        if (newTime >= breakInterval) {
          triggerBreak();
          return 0;
        }
        return newTime;
      });
    }, 1000);

    return () => clearInterval(interval);
  }, [enabled, showBreak, isPaused, breakInterval]);

  // Réinitialiser le timer sur interaction utilisateur
  useEffect(() => {
    const resetTimer = () => {
      if (!showBreak) {
        setActivityTime(0);
      }
    };

    // Réinitialiser sur activité
    window.addEventListener('click', resetTimer);
    window.addEventListener('keydown', resetTimer);
    window.addEventListener('scroll', resetTimer);

    return () => {
      window.removeEventListener('click', resetTimer);
      window.removeEventListener('keydown', resetTimer);
      window.removeEventListener('scroll', resetTimer);
    };
  }, [showBreak]);

  const triggerBreak = useCallback(() => {
    setShowBreak(true);
    setStage(0);
    onBreakStart?.();
    
    // Timeline de l'animation
    const timeline = [
      { delay: 100, action: () => setStage(1) },    // Logo apparaît
      { delay: 2000, action: () => setStage(2) },   // Super-héros arrive
      { delay: 3500, action: () => { setFlash(true); setShake(true); } },
      { delay: 3600, action: () => setFlash(false) },
      { delay: 3700, action: () => setFlash(true) },
      { delay: 3800, action: () => { setFlash(false); setShake(false); } },
      { delay: 4000, action: () => setStage(3) },   // Message
      { delay: 6500, action: () => endBreak() }     // Retour
    ];

    const timers = timeline.map(({ delay, action }) => 
      setTimeout(action, delay)
    );

    return () => timers.forEach(clearTimeout);
  }, [onBreakStart]);

  const endBreak = () => {
    setShowBreak(false);
    setStage(0);
    setActivityTime(0);
    onBreakEnd?.();
  };

  // Permettre de skip avec Escape ou clic
  const handleSkip = () => {
    if (stage >= 2) {
      endBreak();
    }
  };

  const flashColors = ['#FFB703', '#E63946', '#FFFFFF', '#FB8500'];
  const currentFlashColor = flashColors[Math.floor(Math.random() * flashColors.length)];

  // Messages motivationnels
  const breakMessages = [
    "Super travail ! 💪",
    "Tu gères comme un pro ! 🌟",
    "Continue comme ça ! 🚀",
    "Quelle concentration ! 🎯",
    "Tu es sur la bonne voie ! ✨"
  ];
  const randomMessage = breakMessages[Math.floor(Math.random() * breakMessages.length)];

  return (
    <>
      {children}
      
      <AnimatePresence>
        {showBreak && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className={`fixed inset-0 z-[100] flex items-center justify-center overflow-hidden cursor-pointer ${shake ? 'animate-shake' : ''}`}
            style={{ 
              background: flash ? currentFlashColor : 'linear-gradient(135deg, #FFB703 0%, #FB8500 50%, #E63946 100%)' 
            }}
            onClick={handleSkip}
            onKeyDown={(e) => e.key === 'Escape' && handleSkip()}
            tabIndex={0}
          >
            {/* Fond halftone */}
            <div className="absolute inset-0 halftone-bg opacity-20"></div>
            
            {/* Étoiles */}
            <div className="absolute inset-0 overflow-hidden">
              {[...Array(15)].map((_, i) => (
                <motion.div
                  key={i}
                  className="absolute text-white text-2xl"
                  initial={{ opacity: 0, scale: 0 }}
                  animate={{ 
                    opacity: [0, 1, 0],
                    scale: [0, 1, 0],
                    rotate: 360
                  }}
                  transition={{
                    duration: 2,
                    delay: Math.random() * 2,
                    repeat: Infinity
                  }}
                  style={{
                    left: `${Math.random() * 100}%`,
                    top: `${Math.random() * 100}%`,
                  }}
                >
                  ✦
                </motion.div>
              ))}
            </div>

            {/* Stage 1: Logo DailyGlobe ou École */}
            {stage >= 1 && stage < 3 && (
              <motion.div
                key="logo"
                initial={{ scale: 0, rotate: -180, opacity: 0 }}
                animate={{ scale: 1, rotate: 0, opacity: 1 }}
                transition={{ duration: 0.8, type: "spring" }}
                className="absolute z-20"
              >
                <motion.img 
                  src={school.showSchoolLogo ? school.logo : ASSETS.DAILYGLOBE_LOGO}
                  alt="Logo"
                  className="w-48 h-48 object-contain drop-shadow-2xl"
                  animate={{ 
                    y: [0, -10, 0],
                    filter: ['drop-shadow(0 0 20px rgba(255,255,255,0.8))', 
                             'drop-shadow(0 0 40px rgba(255,255,255,1))',
                             'drop-shadow(0 0 20px rgba(255,255,255,0.8))']
                  }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              </motion.div>
            )}

            {/* Stage 2: Super Reporter qui traverse */}
            {stage >= 2 && stage < 3 && (
              <motion.div
                key="hero"
                initial={{ x: '100vw', y: '50vh', rotate: -30 }}
                animate={{ x: '-100vw', y: '30vh', rotate: 15 }}
                transition={{ duration: 1.5, ease: "easeInOut" }}
                className="absolute z-30"
              >
                <img 
                  src={ASSETS.SUPER_REPORTER}
                  alt="Super Reporter"
                  className="w-64 h-64 object-contain"
                  style={{ transform: 'scaleX(-1)' }}
                />
                {/* Traînée de vitesse */}
                <motion.div
                  className="absolute top-1/2 right-0 h-2 bg-gradient-to-l from-transparent via-yellow-300 to-yellow-500"
                  initial={{ width: 0 }}
                  animate={{ width: '200px' }}
                  transition={{ duration: 0.3 }}
                  style={{ transform: 'translateY(-50%)' }}
                />
              </motion.div>
            )}

            {/* Stage 3: Message motivationnel */}
            {stage === 3 && (
              <motion.div
                key="message"
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                exit={{ scale: 0, opacity: 0 }}
                transition={{ type: "spring", duration: 0.5 }}
                className="absolute z-40 text-center px-8"
              >
                <motion.div
                  animate={{ 
                    scale: [1, 1.05, 1],
                    rotate: [-1, 1, -1]
                  }}
                  transition={{ duration: 0.5, repeat: Infinity }}
                  className="bg-white rounded-2xl p-8 shadow-2xl"
                  style={{
                    background: 'linear-gradient(135deg, #ffffff 0%, #f0f0f0 100%)',
                    border: '4px solid #1B4965'
                  }}
                >
                  <div className="text-6xl mb-4">
                    {Math.random() > 0.5 ? '🦸' : '🦸‍♀️'}
                  </div>
                  <h2 
                    className="text-3xl font-black mb-2"
                    style={{ 
                      fontFamily: "'Bangers', cursive",
                      color: '#1B4965',
                      textShadow: '2px 2px 0 #FFB703'
                    }}
                  >
                    PAUSE SUPER-HÉROS !
                  </h2>
                  <p className="text-xl text-gray-700 mb-4">
                    {randomMessage}
                  </p>
                  <p className="text-sm text-gray-500">
                    Cliquez pour continuer...
                  </p>
                </motion.div>
              </motion.div>
            )}

            {/* Skip hint */}
            {stage >= 2 && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                className="absolute bottom-8 text-white text-sm"
              >
                Appuyez sur Échap ou cliquez pour passer
              </motion.p>
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};

export default BreathingBreak;

/**
 * Hook pour utiliser le système de pause dans n'importe quel composant
 */
export const useBreathingBreak = (intervalMinutes = 10) => {
  const [shouldTrigger, setShouldTrigger] = useState(false);
  const [lastActivity, setLastActivity] = useState(Date.now());

  useEffect(() => {
    const checkInterval = setInterval(() => {
      const now = Date.now();
      if (now - lastActivity >= intervalMinutes * 60 * 1000) {
        setShouldTrigger(true);
      }
    }, 30000); // Vérifie toutes les 30 secondes

    const resetActivity = () => {
      setLastActivity(Date.now());
      setShouldTrigger(false);
    };

    window.addEventListener('click', resetActivity);
    window.addEventListener('keydown', resetActivity);

    return () => {
      clearInterval(checkInterval);
      window.removeEventListener('click', resetActivity);
      window.removeEventListener('keydown', resetActivity);
    };
  }, [intervalMinutes, lastActivity]);

  return {
    shouldTrigger,
    resetBreak: () => {
      setShouldTrigger(false);
      setLastActivity(Date.now());
    }
  };
};
